import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { parseDate } from "../utils/dateUtils";
import { ReactNodeDialog } from "../utils/dialogUtlities";
import { Col, Row } from "./Grid";
import TimezoneInput from "./TimezoneInput";
import PropTypes from 'prop-types';
import DateTimeInput from "./DateTimeInput";

/**
 * Dialog to request a date range from user
 * @component 
 */
export const DateRangeDialog = ({show, onClose, onSave, start: startInput, end: endInput, tz: tzInput, min, max, message, showTZ, title, disabled, ...props}) => {
    const { t } = useTranslation();
    const required = true;
    const [start, setStart] = useState(startInput);
    const [end, setEnd] = useState(endInput);
    const [tz, setTZ] = useState(tzInput);

    useEffect(() => {
        setStart(startInput);
    }, [startInput])

    useEffect(() => {
        setEnd(endInput);
    }, [endInput])

    useEffect(() => {
        setTZ(tzInput);
    }, [tzInput])
    
    const invalid = useMemo(() => {
        if (!start || !end) {
            return true;
        }
        
        const startDate = parseDate(start);
        const endDate = parseDate(end);
        const duration = (endDate - startDate) / (1000 * 60 * 60 * 24);

        if (min && duration < min) {
            return t('dateRangeTooShort', { min, duration: parseFloat(duration.toFixed(2)) })
        }
        if (max && duration > max) {
            return t('dateRangeTooLong', { max, duration: parseFloat(duration.toFixed(2)) })
        }
        return false;
    }, [start, end, min, max, t]);

    return <ReactNodeDialog showModal={show} title={title ?? t("selectDateRange")} onSave={()=> onSave(start, end, tz)} onClose={onClose} invalid={invalid} {...props}>
        { message && <Row>
            <Col>{message}</Col>
        </Row> }
        <Row>
            <Col>
                <DateTimeInput disabled={disabled} required={required} tz={tz} placeholder={t('start')} value={start} onChange={setStart} maxDate={end}></DateTimeInput>
            </Col>
        </Row>
        <Row>
            <Col>
                <DateTimeInput disabled={disabled} required={required} tz={tz} placeholder={t('end')} value={end} onChange={setEnd} minDate={start}></DateTimeInput>
            </Col>
        </Row>
        <Row>
            <Col>
               {showTZ && <TimezoneInput disabled={disabled} required={required} value={tz} placeholder={t('timezone')} onChange={setTZ}></TimezoneInput>}
            </Col>
        </Row>
    </ReactNodeDialog>
}

DateRangeDialog.propTypes = {
    /** Controls whether the dialog is visiable */
    show: PropTypes.bool.isRequired,

    /** Function to call when the dialog is closed via the save button. */
    onSave: PropTypes.func,

    /** Function to call when the dialog is closed via the close button. */
    onClose: PropTypes.func,

    /** Title for the dialog to override default */
    title: PropTypes.node,

    /** Text to be displayed by the dialog */
    message: PropTypes.string.isRequired,

    /** Default datetime for dialog range start */
    start: PropTypes.string,

    /** Default datetime for dialog range end */
    end: PropTypes.string,

    /** Timezone for datetimes in dialog */
    tz: PropTypes.string,

    /** Minimum date range duration in days */
    min: PropTypes.number,

    /** Maximum date range duration in days */
    max: PropTypes.number,

    /** Controls whether the tz control is visiable */
    showTZ: PropTypes.bool.isRequired,

    disabled: PropTypes.bool,
}